import React, { useState, useEffect, useRef } from 'react';
import { Phone, MessageSquare, Loader2, Smartphone, SmartphoneCharging } from 'lucide-react';
import { API_URL } from '../config';

const MobilePhoneSync = ({ userId, isDarkMode }) => {
  const [syncStatus, setSyncStatus] = useState({
    hasBothDevices: false,
    syncEnabled: false,
    pcDevice: null,
    mobileDevice: null
  });
  const [currentCompany, setCurrentCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showPairingInput, setShowPairingInput] = useState(false);
  const [pairingCode, setPairingCode] = useState('');
  const [pairingError, setPairingError] = useState('');
  const [deviceId] = useState(() => {
    // Získej nebo vytvoř unikátní ID pro mobilní zařízení
    let id = localStorage.getItem('mobileDeviceId');
    if (!id) {
      id = `mobile-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('mobileDeviceId', id);
    }
    return id;
  });
  
  const eventSourceRef = useRef(null);
  const heartbeatInterval = useRef(null);

  // Registrace mobilního zařízení a heartbeat
  useEffect(() => {
    if (!userId) return;

    const registerDevice = async () => {
      try {
        await fetch(`${API_URL}/device-sync/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            deviceId,
            deviceType: 'mobile'
          })
        });
      } catch (error) {
        console.error('Chyba při registraci mobilního zařízení:', error);
      }
    };

    registerDevice();
    heartbeatInterval.current = setInterval(registerDevice, 30000);

    return () => {
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
      }
    };
  }, [userId, deviceId, syncStatus.syncEnabled]);

  // Načítání statusu synchronizace
  useEffect(() => {
    if (!userId) return;

    const checkStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/device-sync/status?userId=${userId}`);
        if (response.ok) {
          const data = await response.json();
          setSyncStatus(data);
        }
      } catch (error) {
        console.error('Chyba při načítání statusu:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 5000);
    
    return () => clearInterval(interval);
  }, [userId]);

  // Server-Sent Events pro real-time synchronizaci
  useEffect(() => {
    if (!userId || !syncStatus.syncEnabled) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setCurrentCompany(null);
      return;
    }

    setIsConnecting(true);

    // Vytvoř SSE spojení
    const eventSource = new EventSource(
      `${API_URL}/device-sync/stream?userId=${userId}&deviceId=${deviceId}`
    );

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'connected') {
          console.log('✅ Připojeno k synchronizaci');
          setIsConnecting(false);
        } else if (data.type === 'company-opened') {
          console.log('📱 Firma otevřena na PC:', data.companyName);
          // Guard against race: only set company if sync is still enabled
          if (syncStatus.syncEnabled) {
            setCurrentCompany({
              _id: data.companyId,
              name: data.companyName,
              ico: data.companyIco,
              contacts: data.contacts
            });
          }
        } else if (data.type === 'company-closed') {
          console.log('📱 Firma zavřena na PC');
          setCurrentCompany(null);
        } else if (data.type === 'heartbeat') {
          // Heartbeat - udržuj spojení aktivní
        }
      } catch (error) {
        console.error('Chyba při parsování SSE zprávy:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE chyba:', error);
      setIsConnecting(false);
      eventSource.close();
      eventSourceRef.current = null;
    };

    eventSourceRef.current = eventSource;

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [userId, deviceId, syncStatus.syncEnabled]);

  // Odpárování – smaže záznam mobilního zařízení, vynutí nové párování
  const unpairDevice = async () => {
    if (!window.confirm('Opravdu chcete odpojit telefon? Budete muset znovu spárovat pomocí kódu.')) return;
    try {
      await fetch(`${API_URL}/device-sync/unpair`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, deviceId })
      });
    } catch (error) {
      console.error('Chyba při odpárování:', error);
    }
    // Smaž lokální ID, aby se příště vygenerovalo nové
    localStorage.removeItem('mobileDeviceId');
    // Resetuj stav – vrátí se na obrazovku párování
    setSyncStatus({ hasBothDevices: false, syncEnabled: false, pcDevice: null, mobileDevice: null });
    setCurrentCompany(null);
  };

  // Přepnutí synchronizace
  const toggleSync = async () => {
    if (!syncStatus.hasBothDevices) {
      // Zobraz input pro párovací kód
      setShowPairingInput(true);
      return;
    }
    
    try {
      const newSyncState = !syncStatus.syncEnabled;
      
      const response = await fetch(`${API_URL}/device-sync/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          deviceId,
          syncEnabled: newSyncState
        })
      });

      if (response.ok) {
        setSyncStatus(prev => ({
          ...prev,
          syncEnabled: newSyncState,
          mobileDevice: { ...prev.mobileDevice, syncEnabled: newSyncState }
        }));
        
        if (!newSyncState) {
          setCurrentCompany(null);
        }
      }
    } catch (error) {
      console.error('Chyba při přepínání synchronizace:', error);
    }
  };
  
  // Ověření párovacího kódu
  const verifyPairingCode = async () => {
    if (pairingCode.length !== 6) {
      setPairingError('Kód musí mít 6 číslic');
      return;
    }
    
    setPairingError('');
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/device-sync/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          deviceId,
          code: pairingCode
        })
      });

      const data = await response.json();
      if (!data.success) {
        setPairingError(data.error || 'Neplatný kód nebo kód vypršel');
        return;
      }

      setShowPairingInput(false);
      setPairingCode('');

      // Aktualizuj status
      const statusRes = await fetch(`${API_URL}/device-sync/status?userId=${userId}`);
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setSyncStatus(statusData);
      }
      
    } catch (error) {
      console.error('Chyba při párování:', error);
      setPairingError('Chyba při párování. Zkuste to znovu.');
    } finally {
      setIsLoading(false);
    }
  };

  // Funkce pro volání
  const handleCall = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  // Funkce pro SMS
  const handleSMS = (phone) => {
    window.location.href = `sms:${phone}`;
  };

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!syncStatus.hasBothDevices) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
        {/* Header */}
        <div className={`sticky top-0 z-10 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b shadow-sm`}>
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Smartphone className="w-6 h-6 text-gray-400" />
              <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Synchronizace s PC
              </h1>
            </div>
          </div>
        </div>

        {/* Pairing Content */}
        <div className="p-6">
          {!showPairingInput ? (
            <div className={`text-center p-8 rounded-lg ${isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-800'}`}>
              <Smartphone className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h2 className="text-xl font-bold mb-4">Spárování s PC</h2>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                Pro aktivaci této funkce musíte nejdřív spárovat telefon s PC.
              </p>
              
              <button
                onClick={() => setShowPairingInput(true)}
                className="w-full px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition text-lg"
              >
                📱 Zadat párovací kód
              </button>
              
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg text-left">
                <p className="text-sm font-semibold mb-2">Jak spárovat zařízení:</p>
                <ol className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                  <li>1. Otevřete CRM na PC</li>
                  <li>2. Klikněte na ikonku 📱 telefonu (vedle zvonečku)</li>
                  <li>3. Zobrazí se 6-místný kód</li>
                  <li>4. Zde klikněte "Zadat párovací kód" a zadejte ho</li>
                </ol>
              </div>
            </div>
          ) : (
            <div className={`p-8 rounded-lg ${isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-800'}`}>
              <h2 className="text-xl font-bold mb-4 text-center">Zadejte párovací kód z PC</h2>
              
              <div className="mb-6">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={pairingCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setPairingCode(value);
                    setPairingError('');
                  }}
                  placeholder="000000"
                  className={`w-full text-center text-4xl font-bold tracking-widest font-mono p-4 border-2 rounded-lg ${
                    pairingError 
                      ? 'border-red-500 bg-red-50 dark:bg-red-900' 
                      : 'border-blue-500 bg-blue-50 dark:bg-blue-900'
                  } ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                  autoFocus
                />
                
                {pairingError && (
                  <p className="text-red-500 text-sm mt-2 text-center">{pairingError}</p>
                )}
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowPairingInput(false);
                    setPairingCode('');
                    setPairingError('');
                  }}
                  className="flex-1 px-6 py-3 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg font-semibold transition"
                >
                  Zrušit
                </button>
                <button
                  onClick={verifyPairingCode}
                  disabled={pairingCode.length !== 6 || isLoading}
                  className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition"
                >
                  {isLoading ? 'Ověřuji...' : 'Spárovat'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-10 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b shadow-sm`}>
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SmartphoneCharging className={`w-6 h-6 ${syncStatus.syncEnabled ? 'text-green-500 animate-pulse' : 'text-gray-400'}`} />
            <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Synchronizace s PC
            </h1>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={unpairDevice}
              className="px-3 py-2 rounded-lg font-semibold transition bg-red-100 hover:bg-red-200 dark:bg-red-900 dark:hover:bg-red-800 text-red-700 dark:text-red-300 text-sm"
              title="Odpojit a odpárovat telefon"
            >
              🔌 Odpojit
            </button>
            <button
              onClick={toggleSync}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                syncStatus.syncEnabled
                  ? 'bg-green-500 hover:bg-green-600 text-white'
                  : 'bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
              }`}
            >
              {syncStatus.syncEnabled ? '✓ Zapnuto' : 'Vypnuto'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {!syncStatus.syncEnabled ? (
          <div className={`text-center p-8 rounded-lg ${isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-800'}`}>
            <p className="text-gray-500 dark:text-gray-400">
              Zapněte synchronizaci pro zobrazení kontaktů z PC.
            </p>
          </div>
        ) : isConnecting ? (
          <div className={`text-center p-8 rounded-lg ${isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-800'}`}>
            <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              Připojuji se k PC...
            </p>
          </div>
        ) : !currentCompany ? (
          <div className={`text-center p-8 rounded-lg ${isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-800'}`}>
            <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
              <Smartphone className="w-8 h-8 text-blue-500" />
            </div>
            <h2 className="text-xl font-bold mb-2">Čekám na otevření firmy</h2>
            <p className="text-gray-500 dark:text-gray-400">
              Otevřete firmu na PC a kontakty se zobrazí zde.
            </p>
          </div>
        ) : (
          <div>
            {/* Info o firmě */}
            <div className={`mb-4 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
              <h2 className={`text-xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {currentCompany.name}
              </h2>
              {currentCompany.ico && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  IČO: {currentCompany.ico}
                </p>
              )}
            </div>

            {/* Kontakty */}
            <div className="space-y-3">
              {currentCompany.contacts && currentCompany.contacts.length > 0 ? (
                currentCompany.contacts.map((contact, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}
                  >
                    {contact.name && (
                      <h3 className={`font-semibold mb-3 text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {contact.name}
                      </h3>
                    )}
                    
                    <div className="flex gap-3">
                      {contact.phone && (
                        <button
                          onClick={() => handleCall(contact.phone)}
                          className="flex-1 flex flex-col items-center gap-2 p-4 bg-green-500 hover:bg-green-600 text-white rounded-xl transition shadow-md"
                        >
                          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                            <Phone className="w-7 h-7" />
                          </div>
                          <span className="font-semibold text-sm">Zavolat</span>
                          <span className="text-xs opacity-90">{contact.phone}</span>
                        </button>
                      )}
                      
                      {contact.phone && (
                        <button
                          onClick={() => handleSMS(contact.phone)}
                          className="flex-1 flex flex-col items-center gap-2 p-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition shadow-md"
                        >
                          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                            <MessageSquare className="w-7 h-7" />
                          </div>
                          <span className="font-semibold text-sm">SMS</span>
                          <span className="text-xs opacity-90">{contact.phone}</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className={`text-center p-8 rounded-lg ${isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500'}`}>
                  <p>Firma nemá žádné kontakty</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobilePhoneSync;
